/**
 * What the karma controls say, as pure functions (DECISION-37).
 *
 * ⚠️ IT IMPORTS NOTHING FROM REACT OR HEROUI, AND THAT IS WHY IT EXISTS. The
 * frontend test runner is Bun with no DOM, so anything living beside a component
 * is untestable — the same reason `lib/loot.ts` and `lib/errors.ts` are their
 * own files. Everything here is a decision about WORDS, and the words are the
 * part that can be wrong in a way nobody notices.
 *
 * The decision this module exists to get right:
 *
 *   REMAINING === 0 HAS TWO MEANINGS, AND ONLY ONE OF THEM IS ABOUT TODAY.
 *
 * `karmaRemainingToday` is zero on your own row as well as on a row you have
 * spent your two points on, because you can never thank yourself. Telling
 * somebody «на сегодня всё» about their own character promises them something
 * that never arrives, and they will come back tomorrow and find the same thing.
 * `karmaControl` is the one place that distinction is made.
 */

import type { KarmaEntry } from '../data/api.ts';

/** The shape of a roster row this module needs. Deliberately narrow. */
export interface KarmaSubject {
  character: { memberId: number | null };
  karma: number;
  karmaRemainingToday: number;
}

export type KarmaControl =
  /** The viewer's own row. There is no action, ever. */
  | { kind: 'own' }
  /** They may give, and this many points are left today. */
  | { kind: 'available'; remaining: number; hint: string }
  /** They have spent today's points on this person. */
  | { kind: 'spent'; hint: string };

/**
 * Whether to offer the heart, and what its tooltip says.
 *
 * ⚠️ THE `ownMemberId !== null` GUARD IS LOAD-BEARING, and a bare `===` is a bug
 * this codebase has already shipped once. Both sides are nullable: `ownMemberId`
 * is null while `/api/me` resolves, and an admin-created character's `memberId`
 * is null forever (DECISION-33) — so `null === null` marks EVERY unowned
 * character as the viewer's own and hides the heart on the whole population the
 * feature most obviously applies to, on the first paint of every page load.
 */
export function karmaControl(subject: KarmaSubject, ownMemberId: number | null): KarmaControl {
  if (ownMemberId !== null && subject.character.memberId === ownMemberId) {
    return { kind: 'own' };
  }

  if (subject.karmaRemainingToday <= 0) {
    return { kind: 'spent', hint: 'На сегодня очки кармы для него кончились' };
  }

  return {
    kind: 'available',
    remaining: subject.karmaRemainingToday,
    hint: 'Плюс в карму',
  };
}

/**
 * Whether a row shows its karma count at all.
 *
 * ⚠️ THIS RULE HAS BEEN WRONG IN BOTH DIRECTIONS, WHICH IS WHY IT IS A FUNCTION
 * WITH A TEST RATHER THAN A CONDITION IN A COMPONENT.
 *
 * It first hid every zero, on the argument that «0» beside a name reads as a
 * verdict rather than as a starting point. That is true of somebody ELSE's row
 * — and it left the owner unable to see their own karma, because nobody had
 * given them any yet, so the one number they were looking for was the one
 * number the page would not draw.
 *
 * The correction over-shot to drawing every zero, which puts a control on forty
 * rows to answer a question about one.
 *
 * Your own row is the exception because it is the row you scan the list to find
 * — it already carries the «вы» chip for exactly that reason — so a zero there
 * is information and a zero anywhere else is noise.
 */
export function shouldShowKarma(karma: number, isOwn: boolean): boolean {
  return karma > 0 || isOwn;
}

/**
 * How a karma entry names the person who gave it.
 *
 * ⚠️ A BARE NUMERIC ID IS NEVER SHOWN, which is why there is a stated fallback
 * rather than a template string. Karma can be given from the group chat by
 * somebody who has never opened the web app, so this app may hold no name for
 * them at all — «участник» says that, where «42» reports a giver nobody can
 * check. Same rule as `nameActivityShares` in the contract package.
 */
export function karmaGiverLabel(entry: Pick<KarmaEntry, 'fromName' | 'fromUsername'>): string {
  const name = entry.fromName?.trim();
  if (name) return name;
  const handle = entry.fromUsername?.trim();
  if (handle) return `@${handle}`;
  return 'участник';
}

/**
 * The accessible name of the count badge beside a character's name.
 *
 * ⚠️ IT SAYS WHAT THE CONTROL DOES, not what the number is. A reader tabbing the
 * roster hears this instead of seeing a heart, and «3» on its own is a quantity
 * of nothing in particular — the badge is a BUTTON that opens a history, and its
 * name has to say so or the only thing announced is a digit.
 */
export function karmaBadgeLabel(characterName: string, karma: number): string {
  return `Карма ${karma} — за что, ${characterName}`;
}

/**
 * When a point was given, for the history list.
 *
 * DATE ONLY, no time. The question the modal answers is «за что», and a
 * timestamp to the minute invites reading the list as a log of who was quickest
 * rather than of what happened.
 */
export function karmaDateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
